import { Flex, Spacer } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, Formik } from "formik";
import { useCallback, useMemo } from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { FORM_DESCRIPTION_MAX_CHARS } from "src/constants";
import {
  useGetFormTemplateSettingsKey,
  useUpdateFormTemplateSettings,
} from "src/generated/hooks/form-service/queries";
import type { UpdateFormTemplateSettings } from "src/generated/hooks/form-service/requests/types.gen";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useTimezoneName } from "src/hooks/useOrganization";
import useRequiredHasuraRoles from "src/hooks/useRequiredHasuraRoles";
import { validateWithZod } from "src/services/formValidations";
import { HasuraRole } from "src/types/hasuraRole";
import { EditFormTemplateActionBar } from "../components/ActionBar";
import { useFormTemplateContext } from "../formTemplateContext";
import { FormTemplateId, hasChanges, toOriginal } from "../types/common";
import { AdvancedSettings } from "./AdvancedSettings";
import { BasicInfoForm } from "./BasicInfoForm";
import { useEditFormTemplateSettingsContext } from "./context";
import { ImportantDatesForm } from "./ImportantDatesForm";
import {
  FormTemplateSettingsSchema,
  FormTemplateSettingsType,
} from "./schemas";
import { toFormTemplateUpdateBody } from "./utils";

const FORM_ID = "form-template-settings-form";
interface Props {
  isLoading: boolean;
}
export const SettingsForm: React.FC<Props> = ({ isLoading }) => {
  const { state, dispatch } = useEditFormTemplateSettingsContext();
  const { formTemplate } = useFormTemplateContext();

  const id = formTemplate.id;
  const timezoneName = useTimezoneName();
  const toast = useAvelaToast();
  const queryClient = useQueryClient();
  const advancedFormTemplatedEditor = useRequiredHasuraRoles([
    HasuraRole.ADMIN,
    HasuraRole.ORG_ADMIN,
  ]);

  const { mutateAsync: updateSettings } = useUpdateFormTemplateSettings();

  const handleSubmit = useCallback(
    async (values: FormTemplateSettingsType) => {
      try {
        const updateBody = toFormTemplateUpdateBody(values, timezoneName);

        let body: UpdateFormTemplateSettings;

        if (advancedFormTemplatedEditor) {
          body = { ...updateBody };
        } else {
          body = {
            name: updateBody.name,
            description: updateBody.description,
            key: updateBody.key,
            openAt: updateBody.openAt,
            closedAt: updateBody.closedAt,
            reopenAt: updateBody.reopenAt,
            offerStartAt: updateBody.offerStartAt,
          };
        }

        await updateSettings({
          path: { formTemplateId: id },
          body,
        });

        await queryClient.invalidateQueries({
          queryKey: [useGetFormTemplateSettingsKey],
        });

        if (dispatch) {
          dispatch({
            type: "Refresh",
            initialValues: { settings: values },
            formTemplateId: id,
          });
        }

        toast({
          title: "Success",
          description: "Form settings updated",
          status: "success",
          duration: 2000,
        });
      } catch (e) {
        toast({
          title: "Something went wrong",
          description: "Please try again later or contact our team",
          status: "error",
          duration: 2000,
        });
      }
    },
    [
      timezoneName,
      advancedFormTemplatedEditor,
      updateSettings,
      queryClient,
      id,
      dispatch,
      toast,
    ]
  );

  const handleValidate = useCallback(
    (values: FormTemplateSettingsType) => {
      if (dispatch) {
        dispatch({
          type: "UpdateSettings",
          settings: {
            isDraft: true,
            ...values,
          },
          formTemplateId: id,
        });
      }

      return validateWithZod(FormTemplateSettingsSchema)(values);
    },
    [id, dispatch]
  );

  const draft = useMemo(
    () => state?.get("settings")?.get(FormTemplateId(id))?.draft,
    [id, state]
  );

  const original = useMemo(
    () => state?.get("settings")?.get(FormTemplateId(id))?.original,
    [id, state]
  );

  const formikInitialValues = useMemo(() => {
    if (draft) {
      return toOriginal(draft);
    } else {
      return original;
    }
  }, [draft, original]);

  const hasChangesResult = hasChanges(original, draft);

  if (!formikInitialValues) return <GenericError />;

  return (
    <Formik<FormTemplateSettingsType>
      initialValues={formikInitialValues}
      onSubmit={handleSubmit}
      validate={handleValidate}
      enableReinitialize
    >
      {({ values, errors, resetForm }) => {
        const descriptionRemainingChars =
          FORM_DESCRIPTION_MAX_CHARS - values.description.length;

        const handleResetForm = () => {
          resetForm();

          if (dispatch) {
            dispatch({ type: "Reset" });
          }
        };

        return (
          <Flex as={Form} id={FORM_ID} direction="column" gap={2} grow="1">
            <BasicInfoForm
              errors={errors}
              descriptionRemainingChars={descriptionRemainingChars}
            />

            <ImportantDatesForm />

            <AdvancedSettings />
            <Spacer />
            <EditFormTemplateActionBar
              formId={FORM_ID}
              isLoading={isLoading}
              hasChanges={hasChangesResult}
              onClearChanges={handleResetForm}
            />
          </Flex>
        );
      }}
    </Formik>
  );
};
